/* eslint-disable lingui/no-unlocalized-strings, lingui/text-restrictions */
import { ArrowRight, Check, FileText, MessageSquareText, Mic2, Video } from "lucide-react";

export const careerStory = {
  name: "Avery Morgan",
  role: "Product designer",
  portrait: "/images/avery-editorial.webp",
  project: "A clearer first day",
  statement: "Led the redesign of a customer onboarding journey.",
  decision: "People needed a clear next step, not more information.",
  intro:
    "We thought people needed a better guide. Watching them use the product changed my mind. They needed a clearer first step.",
  question: "Tell me about a time research changed your approach.",
  firstAnswer:
    "We redesigned onboarding. I worked with the team, and we made the experience simpler.",
  betterAnswer:
    "In onboarding research, I noticed people hesitated at the first decision. I proposed one guided next step, then tested the prototype with new users before we built it.",
  feedback:
    "Make your own decision visible. Explain what you noticed, what you changed, and how you tested it.",
};

export const StoryIdentity = ({ detail = "Illustrative profile" }: { detail?: string }) => (
  <div className="story-identity">
    <img src={careerStory.portrait} alt="" width="40" height="40" loading="lazy" />
    <span>
      <strong>{careerStory.name}</strong>
      <small>{detail}</small>
    </span>
  </div>
);

export const ResumeArtifact = () => (
  <div className="folio-resume folio-paper">
    <div className="folio-resume-head">
      <span>THE PROFESSIONAL RECORD</span>
      <FileText size={15} />
    </div>
    <h3>{careerStory.name}</h3>
    <p className="folio-resume-role">Product designer · Melbourne</p>
    <div className="folio-resume-rule" />
    <p className="folio-label">Experience</p>
    <h4>Turning complex journeys into clear next steps.</h4>
    <p className="folio-resume-context">
      Research, interaction design, and thoughtful collaboration.
    </p>
    <div className="folio-resume-highlight">
      <span className="folio-label">Selected work / 01</span>
      <p>{careerStory.statement}</p>
    </div>
    <div className="folio-resume-details">
      <span>Discovery</span>
      <span>Prototyping</span>
      <span>Design systems</span>
    </div>
    <footer>
      <span>There is a story behind this line.</span>
      <ArrowRight size={15} />
    </footer>
  </div>
);

export const ProjectArtifact = () => (
  <div className="folio-project">
    <div className="folio-project-top">
      <span>ONBOARDING / CONCEPT STUDY</span>
      <span>01—03</span>
    </div>
    <div className="folio-project-flow">
      <span className="complete">
        <Check size={13} />
      </span>
      <i />
      <span className="current">2</span>
      <i />
      <span>3</span>
    </div>
    <h4>One clear next step.</h4>
    <p>What would you like to do first?</p>
    <div className="folio-project-choice">
      Set up my first project <ArrowRight size={14} />
    </div>
    <span className="folio-project-note">Avery’s illustrative design study</span>
  </div>
);

export const IntroArtifact = () => (
  <div className="folio-intro">
    <div className="folio-intro-image">
      <img
        src={careerStory.portrait}
        alt="Illustrated Avery explaining a design decision"
        width="800"
        height="1000"
        loading="lazy"
      />
      <span>
        <Video size={12} /> SPOTLIGHT INTRO
      </span>
    </div>
    <div className="folio-intro-copy">
      <p>“Watching people use it changed my mind.”</p>
      <span>Avery · illustrative excerpt</span>
    </div>
  </div>
);

export const PracticeArtifact = () => (
  <div className="folio-practice">
    <p className="folio-label">
      <Mic2 size={13} /> Practice interview
    </p>
    <h4>{careerStory.question}</h4>
    <div className="folio-practice-answer">“I noticed people hesitated at the first decision…”</div>
    <p className="folio-practice-note">
      <MessageSquareText size={14} />
      <span>
        Show the decision <strong>you</strong> made.
      </span>
    </p>
  </div>
);

export const ApplicationArtifact = () => (
  <div className="folio-application">
    <div className="folio-application-head">
      <p className="folio-label">The next opportunity</p>
      <span>Applied</span>
    </div>
    <h4>Product designer</h4>
    <p className="folio-application-context">Discovery · Customer experience</p>
    <div className="folio-learning">
      <span className="folio-label">Learning to take forward</span>
      <p>Lead with the insight. Explain your decision.</p>
    </div>
  </div>
);
